import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, CreditCard, Activity, Cpu } from 'lucide-react'
import { clientsAPI, cardsAPI, eventsAPI, devicesAPI } from '../api/client'
import { useOrg } from '../contexts/OrgContext'
import './DashboardPage.css'

function DashboardPage() {
  const { currentOrg } = useOrg()
  const navigate = useNavigate()
  const [stats, setStats] = useState({
    totalClients: 0,
    totalCards: 0,
    activeCards: 0,
    totalEvents: 0,
    totalDevices: 0,
    onlineDevices: 0
  })
  const [recentEvents, setRecentEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (currentOrg?.org_id) {
      loadDashboardData()
    }
  }, [currentOrg?.org_id])

  async function loadDashboardData() {
    if (!currentOrg?.org_id) return

    try {
      setLoading(true)
      setError('')

      // Load all cards with pagination
      let allCards = []
      let nextToken = null
      do {
        const params = {
          org_id: currentOrg.org_id,
          limit: 100
        }
        if (nextToken) {
          params.nextToken = nextToken
        }

        const response = await cardsAPI.getAll(params)
        const cardsData = response.data?.cards || []
        allCards = [...allCards, ...cardsData]
        nextToken = response.data?.nextToken
      } while (nextToken)

      // Load all clients with pagination
      let allClients = []
      nextToken = null
      do {
        const params = { org_id: currentOrg.org_id, limit: 100 }
        if (nextToken) params.nextToken = nextToken
        const response = await clientsAPI.getAll(params)
        const clientsData = response.data?.clients || []
        allClients = [...allClients, ...clientsData]
        nextToken = response.data?.nextToken
      } while (nextToken)

      // Load other stats
      const [eventsRes, devicesRes] = await Promise.all([
        eventsAPI.getAll({ org_id: currentOrg.org_id, limit: 10 }),
        devicesAPI.getAll({ org_id: currentOrg.org_id })
      ])

      // Calculate stats
      const assignedCards = allCards.filter(c => c.status === 'assigned').length
      const onlineDevices = devicesRes.data?.devices?.filter(d => d.status === 'online').length || 0

      setStats({
        totalClients: allClients.length,
        totalCards: allCards.length,
        activeCards: assignedCards,
        totalEvents: eventsRes.data?.count || 0,
        totalDevices: devicesRes.data?.devices?.length || 0,
        onlineDevices
      })

      setRecentEvents(eventsRes.data?.events || [])
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data')
      console.error('Dashboard error:', err)
    } finally {
      setLoading(false)
    }
  }

  function formatTimestamp(timestamp) {
    return new Date(timestamp).toLocaleString()
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Overview of your access control system</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="stats-grid">
        <div className="stat-card" onClick={() => navigate('/clients')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon" style={{ backgroundColor: '#dbeafe' }}>
            <Users size={24} color="#2563eb" />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Clients</div>
            <div className="stat-value">{stats.totalClients}</div>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/cards')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon" style={{ backgroundColor: '#d1fae5' }}>
            <CreditCard size={24} color="#10b981" />
          </div>
          <div className="stat-content">
            <div className="stat-label">Active Cards</div>
            <div className="stat-value">{stats.activeCards} / {stats.totalCards}</div>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/events')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon" style={{ backgroundColor: '#fef3c7' }}>
            <Activity size={24} color="#f59e0b" />
          </div>
          <div className="stat-content">
            <div className="stat-label">Recent Events</div>
            <div className="stat-value">{stats.totalEvents}</div>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/devices')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon" style={{ backgroundColor: '#e0e7ff' }}>
            <Cpu size={24} color="#6366f1" />
          </div>
          <div className="stat-content">
            <div className="stat-label">Devices Online</div>
            <div className="stat-value">{stats.onlineDevices} / {stats.totalDevices}</div>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Recent Activity</h2>
        {recentEvents.length === 0 ? (
          <p className="no-data">No recent events</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Device</th>
                  <th>Card ID</th>
                  <th>Client</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentEvents.map((event) => (
                  <tr key={event.event_id}>
                    <td>{formatTimestamp(event.timestamp)}</td>
                    <td>{event.device_name || event.device_id}</td>
                    <td>{event.card_id}</td>
                    <td>{event.client_name || 'Unknown'}</td>
                    <td>
                      <span className={`badge badge-${
                        event.status === 'active' ? 'success' :
                        event.status === 'inactive' ? 'danger' :
                        'warning'
                      }`}>
                        {event.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default DashboardPage
